import {
    clarifyMaintenance,
    continueMaintenanceTurn,
    createMaintenance,
    streamMaintenanceChange,
} from '~/api/assessment'
import { ApiError } from '~/api/client'
import type { MaintenanceEvent } from '~/api/types'
import type {
    MaintenanceProgress,
    PendingClarification,
    ResultItem,
} from './types'

type MaintenanceProgressReporter = (progress: MaintenanceProgress) => void

type SubmitMaintenanceChangeParams = {
    selectedId: string
    description: string
    pendingClarification: PendingClarification | null
    onProgress?: MaintenanceProgressReporter
}

type SubmitMaintenanceTurnParams = {
    selectedId: string
    eventId: string
    message: string
    onProgress?: MaintenanceProgressReporter
}

type MaintenanceSubmissionResult =
    | {
          type: 'clarification'
          pendingClarification: PendingClarification
      }
    | {
          type: 'created'
          event: MaintenanceEvent
      }

function isMissingClarifyEndpoint(err: unknown): boolean {
    return err instanceof ApiError && err.status === 404
}

export async function submitMaintenanceChange({
    selectedId,
    description,
    pendingClarification,
    onProgress,
}: SubmitMaintenanceChangeParams): Promise<MaintenanceSubmissionResult> {
    if (!pendingClarification) {
        try {
            onProgress?.({
                step: 'clarify',
                title: '변경 내용을 먼저 정리하고 있어요',
                detail: '변경 내용을 분석하기 전에 더 확인할 점이 있는지 살펴보고 있어요.',
            })
            const clarification = await clarifyMaintenance(selectedId, {
                changeDescription: description,
            })
            if (
                clarification.needsClarification &&
                clarification.questions.length > 0
            ) {
                return {
                    type: 'clarification',
                    pendingClarification: {
                        original: description,
                        refinedDescription:
                            clarification.refinedDescription?.trim() ||
                            undefined,
                        questions: clarification.questions,
                        reason: clarification.reason,
                    },
                }
            }
        } catch (err) {
            if (!isMissingClarifyEndpoint(err)) throw err
        }
    }

    const base = pendingClarification
        ? pendingClarification.refinedDescription ||
          pendingClarification.original
        : null
    const changeDescription = base
        ? `${base}\n\n추가 확인:\n${description}`
        : description
    const analyzeProgress = (affectedCount?: number) =>
        onProgress?.({
            step: 'analyze',
            title: '체크리스트 영향 항목을 분석하고 있어요',
            detail: '선택한 평가의 규모, 업종, 분류 범위 안에서 적재된 체크리스트와 매뉴얼 근거를 대조합니다.',
            affectedCount,
        })
    analyzeProgress()
    // 스트리밍으로 항목을 생성되는 대로 표시(체감 지연↓). 스트림 불가/실패 시 비스트리밍 폴백.
    try {
        const event = await streamMaintenanceChange(
            selectedId,
            { changeDescription },
            (p) => analyzeProgress(p.affectedCount)
        )
        return { type: 'created', event }
    } catch {
        const event = await createMaintenance(selectedId, { changeDescription })
        return { type: 'created', event }
    }
}

export async function submitMaintenanceTurn({
    selectedId,
    eventId,
    message,
    onProgress,
}: SubmitMaintenanceTurnParams) {
    onProgress?.({
        step: 'reanalyze',
        title: '후속 내용을 같은 변경건에 반영하고 있어요',
        detail: '이전 분석 결과와 대화 맥락을 유지한 상태로 추가 정보가 점검 항목을 바꾸는지 확인합니다.',
        followUp: true,
    })
    return continueMaintenanceTurn(selectedId, eventId, { message })
}

export function copyRecheckItemsToClipboard(items: ResultItem[]) {
    const lines = items
        .filter((item) => item.recheck_required)
        .map(
            (item) => `- ${item.title ?? item.item_id} (${item.checklistPath})`
        )
    void navigator.clipboard?.writeText(lines.join('\n'))
}
